import React, { useEffect, useRef, useState } from "react";
import LineChartGraph from "./lineChartGraph";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/redux/hooks/hooks";
import StackBarGraph from "./stackBarGraph";
import TreeTableComponent from "./treeTableComponent";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";

import { appInsights } from "../../../../router/router";
import { updateAllocationComplianceDetails } from "../../../../services/redux/reducer/allocationCompliance.reducer";
import { useAllocationComplianceData } from "../../../../react-query/hooks/HCompliance";
import moment from "moment";

const AllocationCompliance = () => {
  const captureRef = useRef(null);
  const AllocationComplianceData = useAppSelector(
    (state) => state.allocationCompliance
  );

  const dispatch = useAppDispatch();

  const { allocationData, allocationDataLoaded, isEstimated, mtdIsEstimated } =
    useAllocationComplianceData(
      AllocationComplianceData.filter.commodity,
      AllocationComplianceData.filter.district,
      AllocationComplianceData.filter.baseline,
      AllocationComplianceData.filter.trendView,
      AllocationComplianceData.filter.baseSku,
      AllocationComplianceData.isPartnerView,
      localStorage.getItem("userEmail")
    );

  useEffect(() => {
    dispatch(
      updateAllocationComplianceDetails({
        ...AllocationComplianceData,
        dataLoaded: allocationDataLoaded,
        isData:
          allocationData?.purchaseLbs &&
          allocationData?.PoCost &&
          allocationData?.MTDSummary
            ? true
            : false,
      })
    );
  }, [
    AllocationComplianceData.isPartnerView,
    AllocationComplianceData.filter.commodity,
    AllocationComplianceData.filter.district,
    AllocationComplianceData.filter.baseline,
    AllocationComplianceData.filter.trendView,
    allocationDataLoaded,
  ]);

  useEffect(() => {
    if (AllocationComplianceData.isDownload) {
      handleExcelDownload();
      dispatch(
        updateAllocationComplianceDetails({
          ...AllocationComplianceData,
          isDownload: false,
        })
      );
    }
  }, [AllocationComplianceData.isDownload]);

  const getFullYearFromYY = (year: number) => {
    const shortYear = Number(year);
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const guessedYear = currentCentury + shortYear;

    // If guessedYear is more than 50 years ahead, subtract 100
    return guessedYear - currentYear > 50 ? guessedYear - 100 : guessedYear;
  };

  const handleExcelDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Supplier Purchase Details");

    // Add headers and rows based on structure
    worksheet.addRow(["Commodity:", AllocationComplianceData.filter.commodity]);
    worksheet.addRow([
      "District:",
      AllocationComplianceData.filter.district.length
        ? AllocationComplianceData.filter.district.join(", ")
        : "All",
    ]);
    worksheet.addRow(["Baseline:", AllocationComplianceData.filter.baseline]);
    worksheet.addRow([
      "Trend View:",
      AllocationComplianceData.filter.trendView,
    ]);
    worksheet.addRow(["Base SKU", AllocationComplianceData.filter.baseSku]);

    if (!captureRef.current) {
      console.error("Capture element not found.");
    } else {
      // Take screenshot using html2canvas
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: "#fff", // or any solid color you want
      });
      const dataUrl = canvas.toDataURL("image/png");

      // Add some text
      worksheet.addRow([
        "Supplier Market Share",
        AllocationComplianceData.isPartnerView
          ? "Partner vs Others"
          : "All Supplier View",
      ]);
      // worksheet.addRow([
      //   `Vendor Market Share ${AllocationComplianceData.filter.baseline}`,
      // ]);

      // Add the screenshot to the workbook

      const imageId = workbook.addImage({
        base64: dataUrl,
        extension: "png",
      });

      // Place the image in the worksheet
      worksheet.addImage(imageId, {
        tl: { col: 0, row: 7 }, // Top-left corner (A8 is row 7 in 0-based indexing)
        ext: { width: 1200, height: 300 }, // Dimensions in pixels
      });
      while (worksheet.rowCount < 24) {
        worksheet.addRow([]);
      }
    }

    worksheet.mergeCells("A24:E24");
    worksheet.getCell("A24").value = isEstimated
      ? `In the line Chart, Triangle denotes estimated value using supplier quote for ${AllocationComplianceData.filter.baseSku}, Rhombus denotes estimated value using SKU ratio analysis`
      : "";

    //Table start for MTS summary
    worksheet.mergeCells(
      `A26:${String.fromCharCode(
        "A".charCodeAt(0) +
          3 +
          (allocationData?.MTDSummary.vendors.length
            ? allocationData.MTDSummary.vendors.length
            : 0) -
          1
      )}26`
    );

    worksheet.getCell(
      "A26"
    ).value = `${allocationData?.MTDSummary.month} MTD Summary for ${AllocationComplianceData.filter.commodity}`;

    //adding headers
    worksheet.addRow([
      "",
      ...(allocationData?.MTDSummary?.vendors
        ? allocationData.MTDSummary.vendors
        : []),
      "Total",
      "GAINS Monthly Forecast",
    ]);

    //adding values for MTS Summary
    if (
      allocationData &&
      allocationData?.MTDSummary &&
      allocationData?.MTDSummary?.data &&
      allocationData?.MTDSummary?.vendors
    ) {
      allocationData?.MTDSummary?.data?.forEach((ele: any) => {
        worksheet.addRow([
          `${ele.key === "0" ? allocationData.MTDSummary.month : ""} ${
            ele.name
          } ${
            ele.key === "0"
              ? AllocationComplianceData.isPartnerView
                ? AllocationComplianceData?.filter?.district?.length
                  ? `for ${AllocationComplianceData?.filter?.district[0]}`
                  : ""
                : AllocationComplianceData.filter.district.length === 1
                ? `for ${AllocationComplianceData.filter.district[0]}`
                : AllocationComplianceData.filter.district.length === 0
                ? `for All Districts`
                : `for Selected Districts`
              : ""
          }`,
          ...allocationData?.MTDSummary?.vendors?.map(
            (vendor: (typeof allocationData.MTDSummary.vendors)[number]) =>
              ele[vendor] !== "-"
                ? ele.key === "1" || ele.key === "3"
                  ? Number(ele[vendor].slice(0, -1)) / 100
                  : Number(ele[vendor])
                : ele[vendor]
          ),
          ele.Total !== ""
            ? ele.key === "1" || ele.key === "3"
              ? Number(ele.Total.slice(0, -1)) === 0
                ? "-"
                : Number(ele.Total.slice(0, -1)) / 100
              : Number(ele.Total)
            : "-",
          ele.GainsMonthlyForecast !== null
            ? ele.key === "1" || ele.key === "3"
              ? Number(ele.GainsMonthlyForecast.slice(0, -1)) / 100
              : Number(ele.GainsMonthlyForecast)
            : ele.GainsMonthlyForecast,
        ]);
        if (ele.children) {
          ele.children.forEach((childEle: any) =>
            worksheet.addRow([
              childEle.name,
              ...allocationData?.MTDSummary?.vendors?.map(
                (vendor: (typeof allocationData.MTDSummary.vendors)[number]) =>
                  childEle[vendor]
              ),
              childEle.Total,
              childEle.GainsMonthlyForecast,
            ])
          );
        }
      });
    }

    //Table end for MTS summary

    //Table start for Purchase Lbs

    const endcellValue =
      "A".charCodeAt(0) +
      1 +
      (allocationData?.purchaseLbsDownloadData.vendors.length
        ? allocationData.purchaseLbsDownloadData.vendors.length
        : 0) -
      1;

    worksheet.mergeCells(`A38:${String.fromCharCode(endcellValue)}38`);

    worksheet.getCell(
      "A38"
    ).value = `${AllocationComplianceData.filter.commodity} Purchase Lbs.`;

    //Adding headers
    worksheet.addRow([
      "Purchase LBS",
      ...(allocationData?.purchaseLbsDownloadData?.vendors
        ? allocationData.purchaseLbsDownloadData.vendors
        : []),
    ]);

    // Adding values for Purchase Lbs
    if (
      allocationData &&
      allocationData?.purchaseLbsDownloadData &&
      allocationData?.purchaseLbsDownloadData?.data &&
      allocationData?.purchaseLbsDownloadData?.vendors
    ) {
      allocationData?.purchaseLbsDownloadData?.data?.forEach((ele: any) =>
        worksheet.addRow([
          `${ele.month.substring(
            0,
            ele.month.lastIndexOf("-")
          )}-${getFullYearFromYY(
            ele.month.substring(ele.month.lastIndexOf("-") + 1)
          )}`,
          ...allocationData?.purchaseLbsDownloadData?.vendors?.map(
            (
              vendor: (typeof allocationData.purchaseLbsDownloadData.vendors)[number]
            ) => (ele[vendor] !== 0 && ele[vendor] !== null ? ele[vendor] : "-")
          ),
        ])
      );
    }

    //Table end for Purchase Lbs

    //Table start for PO Cost

    const poTableStartCellNumber =
      38 +
      1 +
      (allocationData?.purchaseLbsDownloadData?.data
        ? allocationData?.purchaseLbsDownloadData.data.length
        : 0) +
      1 +
      1;

    const poTableEndCellValue =
      "A".charCodeAt(0) +
      1 +
      (allocationData?.PoCost.vendors.length
        ? allocationData.PoCost.vendors.length
        : 0) +
      1 -
      1;

    worksheet.mergeCells(
      `A${poTableStartCellNumber}:${String.fromCharCode(
        poTableEndCellValue
      )}${poTableStartCellNumber}`
    );

    worksheet.getCell(
      `A${poTableStartCellNumber}`
    ).value = `${AllocationComplianceData.filter.baseSku} PO Cost`;

    //adding headers
    worksheet.addRow([
      `${AllocationComplianceData.filter.baseSku} PO Cost`,
      ...(allocationData?.PoCost?.vendors ? allocationData.PoCost.vendors : []),
      "Board Cost",
    ]);

    //adding values for PO Cost
    if (
      allocationData?.PoCost &&
      allocationData?.PoCost?.data &&
      allocationData?.PoCost?.vendors
    ) {
      allocationData?.PoCost?.data?.forEach((ele: any) =>
        worksheet.addRow([
          //        dateMonth.substring(0, dateMonth.lastIndexOf("-")),
          // "-",
          // getFullYearFromYY(dateMonth.substring(dateMonth.lastIndexOf("-") + 1))
          `${ele.month.substring(
            0,
            ele.month.lastIndexOf("-")
          )}-${getFullYearFromYY(
            ele.month.substring(ele.month.lastIndexOf("-") + 1)
          )}`,
          ...allocationData?.PoCost?.vendors?.map(
            (vendor: (typeof allocationData.PoCost.vendors)[number]) =>
              ele[`${vendor}_actualPoCost`] !== null &&
              Number(ele[`${vendor}_actualPoCost`]) !== 0
                ? ele[`${vendor}_actualPoCost`]
                : ele[`${vendor}_estimatedPoCost`] !== null &&
                  Number(ele[`${vendor}_estimatedPoCost`]) !== 0
                ? ele[`${vendor}_estimatedPoCost`]
                : "-"
          ),
          ele["Board Cost"],
        ])
      );
    }

    //Table end for PO Cost

    //Table start for TM %

    const TMTableStartCellNumber =
      poTableStartCellNumber +
      1 +
      (allocationData?.PoCost?.data ? allocationData?.PoCost?.data.length : 0) +
      1 +
      1;

    const TMTableEndCellValue = "A".charCodeAt(0) + 1;
    // +
    // (allocationData?.PoCost.vendors.length
    //   ? allocationData.PoCost.vendors.length
    //   : 0) +
    // 1 -
    // 1;

    worksheet.mergeCells(
      `A${TMTableStartCellNumber}:${String.fromCharCode(
        TMTableEndCellValue
      )}${TMTableStartCellNumber}`
    );

    worksheet.getCell(`A${TMTableStartCellNumber}`).value = `TM%`;

    //adding headers
    // worksheet.addRow([
    //   "Month",
    //   "Percentage",
    // ]);

    //adding values for PO Cost
    if (
      allocationData?.purchaseLbsDownloadData &&
      allocationData?.purchaseLbsDownloadData?.tmData
    ) {
      allocationData?.purchaseLbsDownloadData?.tmData?.forEach((ele: any) =>
        worksheet.addRow([ele["month"], ele["tmPercentage"] / 100])
      );
    }

    //Table end for TM %

    //Table start for Growth
    const GrowthTableStartCellNumber =
      TMTableStartCellNumber +
      (allocationData?.purchaseLbsDownloadData.tmData
        ? allocationData?.purchaseLbsDownloadData.tmData.length
        : 0) +
      1 +
      1;

    const GrowthTableEndCellValue = "A".charCodeAt(0) + 2;
    worksheet.mergeCells(
      `A${GrowthTableStartCellNumber}:${String.fromCharCode(
        GrowthTableEndCellValue
      )}${GrowthTableStartCellNumber}`
    );

    worksheet.getCell(
      `A${GrowthTableStartCellNumber}`
    ).value = `Sales Volume Growth Summary`;

    //adding headers
    worksheet.addRow([
      "",
      `${allocationData?.purchaseLbsDownloadData?.volumeGrowth?.district}`,
      "National Average",
    ]);

    //adding values for PO Cost
    if (
      allocationData?.purchaseLbsDownloadData &&
      allocationData?.purchaseLbsDownloadData?.volumeGrowth
    ) {
      worksheet.addRow([
        "YTD Volume Growth",
        allocationData?.purchaseLbsDownloadData?.volumeGrowth["volGrowthVal"] /
          100,
        allocationData?.purchaseLbsDownloadData?.volumeGrowth["nationalAvg"] /
          100,
      ]);
    }

    worksheet.getCell(
      `A${GrowthTableStartCellNumber + 1 + 1 + 1 + 1}`
    ).value = `Projects Running through stock are included in the data as we do not have a way to systematically distinguish between project blanket releases going through stock and standard stock purchases`;

    if (allocationData?.purchaseLbsDownloadData?.othersVendors?.length) {
      worksheet.getCell(
        `A${GrowthTableStartCellNumber + 1 + 1 + 1 + 1 + 1}`
      ).value = `OTHERS - ${allocationData.purchaseLbsDownloadData.othersVendors.join(", ")}`;
    }

    //Table end for Growth

    // Apply styling to all data rows
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber <= 6) {
        row.getCell(1).font = { bold: true }; // Make the first cell bold
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      }

      if (rowNumber >= 25) {
        row.getCell(1).font = {
          bold: rowNumber >= 28 && rowNumber <= 36 && true,
        };

        row.font = {
          bold:
            (rowNumber === 26 ||
              rowNumber === 27 ||
              rowNumber === 38 ||
              rowNumber === 39 ||
              rowNumber === poTableStartCellNumber ||
              rowNumber === poTableStartCellNumber + 1 ||
              rowNumber === TMTableStartCellNumber ||
              rowNumber === GrowthTableStartCellNumber ||
              rowNumber === GrowthTableStartCellNumber + 1) &&
            true,
        };

        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.alignment = {
            horizontal:
              rowNumber === 26 ||
              rowNumber === 27 ||
              rowNumber === 38 ||
              rowNumber === 39 ||
              rowNumber === poTableStartCellNumber ||
              rowNumber === poTableStartCellNumber + 1 ||
              rowNumber === TMTableStartCellNumber ||
              rowNumber === GrowthTableStartCellNumber ||
              rowNumber === GrowthTableStartCellNumber + 1
                ? "center"
                : "left",
          };
          if (
            rowNumber !== GrowthTableStartCellNumber + 1 + 1 + 1 + 1 &&
            rowNumber !== GrowthTableStartCellNumber + 1 + 1 + 1 + 1 + 1
          ) {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          }
        });
        if (rowNumber >= 28 && rowNumber <= 37) {
          for (let i = 2; i <= 16; i++) {
            row.getCell(i).alignment = { horizontal: "right" };
            const text = row.getCell(1).value;
            if (
              text &&
              typeof text === "string" &&
              (text.trim() === "Market Share %" ||
                text.trim() === "Cost Premium vs. Partner")
            ) {
              row.getCell(i).numFmt = "0.00%";
            } else if (
              text &&
              typeof text === "string" &&
              text.trim().startsWith("Avg") &&
              text.trim().endsWith("Cost")
            ) {
              row.getCell(i).numFmt = "$#,##0.00";
            } else {
              row.getCell(i).numFmt = "#,##0";
            }
          }
        }
        if (
          rowNumber >= 40 &&
          rowNumber <= 40 + allocationData?.purchaseLbsDownloadData.data.length
        ) {
          // row.getCell(1).numFmt = "MM-DD-YYYY";
          for (
            let i = 2;
            i <= 2 + allocationData?.purchaseLbsDownloadData?.vendors.length;
            i++
          ) {
            row.getCell(i).numFmt = "#,##0";
            row.getCell(i).alignment = { horizontal: "right" };
          }
        }
        if (
          rowNumber >= poTableStartCellNumber + 2 &&
          rowNumber <=
            poTableStartCellNumber + 2 + allocationData.PoCost.data.length
        ) {
          for (
            let i = 2;
            i <= 2 + allocationData?.PoCost?.vendors.length + 1;
            i++
          ) {
            row.getCell(i).numFmt = "$#,##0.00";
            row.getCell(i).alignment = { horizontal: "right" };
          }
        }
        if (
          rowNumber >= TMTableStartCellNumber + 1 &&
          rowNumber <=
            TMTableStartCellNumber +
              1 +
              allocationData.purchaseLbsDownloadData.tmData.length
        ) {
          row.getCell(1).alignment = { horizontal: "left" };
          row.getCell(2).alignment = { horizontal: "right" };
          row.getCell(2).numFmt = "0.00%";
        }
        if (
          rowNumber >= GrowthTableStartCellNumber + 1 &&
          rowNumber <= GrowthTableStartCellNumber + 1 + 1
        ) {
          row.getCell(1).alignment = { horizontal: "left" };
          row.getCell(2).alignment = { horizontal: "right" };
          row.getCell(3).alignment = { horizontal: "right" };
          row.getCell(2).numFmt = "0.00%";
          row.getCell(3).numFmt = "0.00%";
        }
      }
    });

    worksheet.columns.forEach((column: any, i: number) => {
      // let maxLength = 0;
      // column.eachCell({ includeEmpty: true }, (cell: any, j: number) => {
      //   if (cell.value) {
      //     const cellValue = cell.value.toString();
      //     maxLength = Math.max(maxLength, cellValue.length);
      //   }
      // });
      if (i === 0) {
        column.width = 30;
      } else {
        column.width = 25;
      } // Add some padding to the width
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `Supplier Market Share_${
        AllocationComplianceData.filter.commodity
      }_${moment(new Date()).format("MM-DD-YYYY")}_${
        AllocationComplianceData.isPartnerView
          ? "Partner Supplier"
          : "All Supplier"
      }.xlsx`
    );
    appInsights.trackEvent({
      name: "Volume Allocation: Supplier Market Share",
      properties: {
        description: `${localStorage.getItem(
          "userEmail"
        )} has downloaded "Supplier Market Share_${
          AllocationComplianceData.filter.commodity
        }.xlsx" Report`,
      },
    });
  };

  const getActiveVendors = (obj: any) => {
    if (obj?.data !== undefined && obj?.vendors !== undefined) {
      return obj.vendors.filter((vendor: string) =>
        obj.data.some((entry: any) =>
          (entry[vendor] && entry[vendor] !== 0) ||
          entry[`${vendor}_actualPoCost`] != null
            ? entry[`${vendor}_actualPoCost`] !== 0
              ? true
              : false
            : false || entry[`${vendor}_estimatedPoCost`] != null
            ? entry[`${vendor}_estimatedPoCost`] !== 0
              ? true
              : false
            : false
        )
      );
    }
  };

  const rectSvg = `<svg
                    width="20"
                    height="20"
                    x={cx - 10}
                    y={cy - 10}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <polygon
                      points="10,6 14,10 10,14 6,10"
                      fill="#E15759"
                      stroke="#E15759"
                      strokeWidth="1"
                    />
                  </svg>`;
  const triangleSvg = `<svg
                      width="20"
                      height="20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <polygon
                        points="10,6 14,14 6,14"
                        fill="#E15759"
                      stroke="#E15759"
                        stroke-width="1"
                      />
                    </svg>`;

  return (
    <div className="bg-[#F1F1F1] grow shrink p-[0.5%] flex flex-col flex-nowrap justify-start items-start gap-6 h-[77%] w-full">
      {AllocationComplianceData.isData ? (
        <div className="grid grid-cols-2 grid-rows-12 h-full w-full p-2">
          <div
            ref={captureRef}
            className={`canvasRef col-span-2 h-full row-span-7 flex gap-2 z-[1]`}
          >
            <div className="w-[50%] h-full">
              {allocationData?.purchaseLbs && (
                <StackBarGraph
                  commodity={AllocationComplianceData.filter.commodity}
                  vendors={getActiveVendors(allocationData?.purchaseLbs) || []}
                  data={allocationData?.purchaseLbs.data || []}
                  name={`${AllocationComplianceData.filter.commodity} Market Share by Purchase lbs`}
                  partnerVendor={
                    AllocationComplianceData.isPartnerView
                      ? AllocationComplianceData?.filter?.district?.length
                        ? AllocationComplianceData?.filter?.district[0]
                        : ""
                      : ""
                  }
                  decimal={1}
                  unit={"%"}
                  isdownload={AllocationComplianceData.isDownload}
                  tmData={allocationData?.purchaseLbsDownloadData?.tmData || []}
                  trendView={AllocationComplianceData.filter.trendView}
                  othersVendors={allocationData?.purchaseLbs?.othersVendors || []}
                />
              )}
            </div>
            <div className="flex flex-col h-full w-[50%]">
              {allocationData?.PoCost && (
                <LineChartGraph
                  commodity={AllocationComplianceData.filter.commodity}
                  baseSKU={AllocationComplianceData.filter.baseSku}
                  vendors={getActiveVendors(allocationData?.PoCost) || []}
                  data={allocationData?.PoCost.data || []}
                  name={`${AllocationComplianceData.filter.baseSku} PO Cost`}
                  partnerVendor={
                    AllocationComplianceData.isPartnerView
                      ? AllocationComplianceData?.filter?.district?.length
                        ? AllocationComplianceData?.filter?.district[0]
                        : ""
                      : ""
                  }
                  isEstimated={isEstimated}
                  decimal={2}
                  unit={"$"}
                  isdownload={AllocationComplianceData.isDownload}
                  trendView={AllocationComplianceData.filter.trendView}
                  othersVendors={allocationData?.PoCost?.othersVendors || []}
                />
              )}
            </div>
          </div>

          <div className="col-span-2 h-full row-span-5">
            <div className=" flex justify-between font-bold pl-[1.2vh] pt-[1.2vh] pb-[0.5vh] text-[1.9vh] h-[5%]">
              <div>
                <span>
                  <span className="text-[#86BC25]">
                    {(allocationData?.MTDSummary &&
                      allocationData?.MTDSummary?.month &&
                      allocationData?.MTDSummary.month) ??
                      ""}
                  </span>
                </span>
                <span> MTD Summary</span>
              </div>
            </div>
            {allocationData?.MTDSummary && (
              <TreeTableComponent
                data={allocationData?.MTDSummary.data || []}
                month={allocationData?.MTDSummary.month || ""}
                vendors={allocationData?.MTDSummary.vendors || []}
                partnerVendor={
                  AllocationComplianceData.isPartnerView
                    ? AllocationComplianceData?.filter?.district?.length
                      ? AllocationComplianceData?.filter?.district[0]
                      : ""
                    : ""
                }
                district={AllocationComplianceData.filter.district}
                isEstimated={mtdIsEstimated}
                othersVendors={allocationData?.MTDSummary?.othersVendors || []}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="shadow-module-card flex justify-center items-center h-full w-full p-2 bg-[white] text-fs32">
          No Data available. Please change filters to view data
        </div>
      )}
    </div>
  );
};

export default AllocationCompliance;
